/**
 * This component makes up the bank infomration fields of the Vendor Application
 */
import { drop } from 'lodash';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Button from '@mui/material/Button';
import {
  isValidAccountNumber,
  isValidRoutingNumber,
} from '../../Lib/Utils/FormUtils/vendor-application';
import styles from '../VendorApplication/VendorApplication.module.css';
import TextInput from '../ReusableComponents/TextInput/TextInput';
import ReEnterBankField from './ReEnterBankField';

/**
 * Masks everything but the last 4 digits of banking information on the
 * application after it is submitted.
 *
 * @param {string} value the field value
 * @param {boolean} disabled the disabled state of the input. defaults to false
 */
const maskAccountInfo = (value, disabled = false) => {
  if (!disabled || !value) return value;
  return value.replace(/\d(?=\d{4})/g, 'X');
};

function BankFields(props) {
  const {
    disabled,
    prefix,
    domestic,
    header,
    showUpdateButton = false,
    required = false,
    emptyFields,
    banking_request_status = null,
    names: {
      account_name_field,
      bank_name_field,
      routing_number_field,
      account_number_field,
    },
    values: { account_name, bank_name, routing_number, account_number },
    onUpdateButtonClick = null,
    isInfluencer = false,
    validationErrors,
  } = props;

  const [state, setState] = useState({
    account_name,
    bank_name,
    routing_number,
    account_number,
  });

  useEffect(() => {
    setState({ ...props.values });
  }, [props.values]);

  /**
   * When moving focus off of an input, applies validation to fields in real
   * time via onBlur function in props
   *
   * @param {object} target: {
   *  @property {string} name: the input name,
   *  @property {string} value: the input value
   *    }
   */
  const onBlur = ({ target: { name, value } }) => {
    props.onBlur({ target: { name, value } });
  };

  /**
   * Updates local state with the input values to control the inputs
   *
   * @param {object} target: {
   *   @property {string} name: the input name
   *   @property {string} value: the input value
   *    }
   */
  const onChange = (e) => {
    if (!e.target) return;
    const { name, value } = e.target;
    const nameArr = name.split('_');
    const key = nameArr.length > 2 ? drop(nameArr, 1).join('_') : name;
    setState((prevState) => ({ ...prevState, [key]: value }));
    props.onChange(name, value);
  };

  const buildBankFieldName = (
    { isPlural, withNumberSuffix } = {
      isPlural: false,
      withNumberSuffix: false,
    },
  ) => {
    if (!props.domestic) {
      if (isInfluencer) {
        return isPlural ? 'IBAN / Account Numbers' : 'IBAN / Account Number';
      }
      if (withNumberSuffix) {
        return isPlural ? 'IBAN Numbers' : 'IBAN Number';
      }
    }
    return isPlural ? 'Account Numbers' : 'Account Number';
  };

  const isBankingRequestPending = banking_request_status === 'Pending Approval';

  return (
    <div>
      <h4 className={styles.subHeader}>
        <span>{header}</span>
        {onUpdateButtonClick && disabled && showUpdateButton && (
          <Button
            onClick={onUpdateButtonClick}
            variant="outlined"
            color="primary"
            size="small"
            disabled={isBankingRequestPending}
          >
            {isBankingRequestPending
              ? 'Request Pending Approval'
              : 'Request Banking Update'}
          </Button>
        )}
      </h4>
      <div className="flex flex-col md:flex-row gap-4 pb-4">
        <TextInput
          fullWidth
          required={required}
          error={
            required &&
            (!!emptyFields[account_name_field] ||
              !!validationErrors?.[account_name_field])
          }
          label={
            isInfluencer ? 'Beneficiary Name on Account' : 'Name on Account'
          }
          variant="outlined"
          onChange={onChange}
          onBlur={onBlur}
          name={account_name_field}
          value={state.account_name || ''}
          id={`${prefix}-account-name`}
          autoComplete="no"
          disabled={disabled}
          className={styles.rowItem}
          formHelperText={validationErrors?.[account_name_field]}
        />

        <TextInput
          fullWidth
          required={required}
          error={
            required &&
            (!!emptyFields[bank_name_field] ||
              !!validationErrors?.[bank_name_field])
          }
          label="Bank Name"
          variant="outlined"
          onChange={onChange}
          onBlur={onBlur}
          name={bank_name_field}
          value={state.bank_name || ''}
          id={`${prefix}-bank-name`}
          autoComplete="no"
          disabled={disabled}
          className={styles.rowItem}
          formHelperText={validationErrors?.[bank_name_field]}
        />
      </div>

      <div className="flex flex-col md:flex-row gap-4 pb-4">
        <TextInput
          fullWidth
          className={styles.rowItem}
          required={required}
          error={
            (required && !!emptyFields[account_number_field]) ||
            !isValidAccountNumber(state.account_number, domestic) ||
            !!validationErrors?.[account_number_field]
          }
          label={buildBankFieldName()}
          variant="outlined"
          name={account_number_field}
          value={maskAccountInfo(state.account_number, disabled) || ''}
          id={`${prefix}-account-number`}
          onChange={onChange}
          onBlur={onBlur}
          onPaste={(e) => e.preventDefault()}
          autoComplete="no"
          disabled={disabled}
          pattern="[a-zA-Z0-9\s]+"
          formHelperText={validationErrors?.[account_number_field]}
        />

        <TextInput
          fullWidth
          className={styles.rowItem}
          required={required}
          error={
            (required && !!emptyFields[routing_number_field]) ||
            !isValidRoutingNumber(state.routing_number, domestic) ||
            !!validationErrors?.[routing_number_field]
          }
          label={domestic ? 'Routing Number' : 'Swift Code'}
          variant="outlined"
          onChange={onChange}
          onBlur={onBlur}
          onPaste={(e) => e.preventDefault()}
          name={routing_number_field}
          value={maskAccountInfo(state.routing_number, disabled) || ''}
          id={`${prefix}-routing-number`}
          autoComplete="no"
          disabled={disabled}
          formHelperText={validationErrors?.[routing_number_field]}
        />
      </div>

      {!disabled && (
        <div className="flex flex-col md:flex-row gap-4">
          <ReEnterBankField
            label={`Re-Enter ${buildBankFieldName()}`}
            name="reEnterAccountNumber"
            onChange={props.onChange}
            onBlur={props.onBlur}
            parentValue={state.account_number}
            formHelperText={`Please make sure your ${buildBankFieldName({
              isPlural: true,
              withNumberSuffix: true,
            })} match.`}
          />

          <ReEnterBankField
            label={domestic ? 'Re-Enter Routing Number' : 'Re-Enter Swift Code'}
            name="reEnterRoutingNumber"
            onChange={props.onChange}
            onBlur={props.onBlur}
            parentValue={state.routing_number}
            formHelperText={`Please make sure your ${
              domestic ? 'Routing' : 'Swift'
            } codes match.`}
          />
        </div>
      )}
    </div>
  );
}

BankFields.propTypes = {
  values: PropTypes.shape({
    bank_name: PropTypes.string,
    account_name: PropTypes.string,
    account_number: PropTypes.string,
    routing_number: PropTypes.string,
  }).isRequired,
  names: PropTypes.shape({
    bank_name_field: PropTypes.string,
    account_name_field: PropTypes.string,
    account_number_field: PropTypes.string,
    routing_number_field: PropTypes.string,
  }).isRequired,
  prefix: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  onUpdateButtonClick: PropTypes.func,
  showUpdateButton: PropTypes.bool,
  disabled: PropTypes.bool.isRequired,
  domestic: PropTypes.bool.isRequired,
  header: PropTypes.string.isRequired,
  emptyFields: PropTypes.shape({}).isRequired,
  banking_request_status: PropTypes.string,
  required: PropTypes.bool,
  isInfluencer: PropTypes.bool,
  validationErrors: PropTypes.shape({}),
};

export default BankFields;
